import _ from "lodash";
import { isLabelNode } from "./KonvaLabel";
import { TEXT_ELEMENT_CONFIG } from "../KonvaConfig";
import { CANVAS_IDS, KONVA_ATTR, KONVA_EVENTS } from "../KonvaConstants";
import {
  isTextRTL,
  generateElementName,
  getCanvasRelativeSize,
} from "../../KonvaCommonUtils";
import { isWorksheetWebView } from "Worksheet/modules/WorksheetUtils";

/**
 *
 * @param {*} item
 * @param {*} konva
 * @returns New shape instance
 */
export const createTextNode = (
  item,
  { stage, layer, transformer, konva } = {}
) => {
  const id = _.get(item, "id");
  const canDrag = _.isEqual(_.get(item, "category"), "DRAGGABLE");

  const isRTL = isTextRTL(_.get(item, "text", ""));

  const props = {
    id: id,
    x: _.get(item, "x"),
    y: _.get(item, "y"),
    fill: TEXT_ELEMENT_CONFIG.color,
    name: generateElementName(item),
    padding: _.get(item, "fontSize", TEXT_ELEMENT_CONFIG.fontSize) / 2,
    fontStyle: TEXT_ELEMENT_CONFIG.fontStyle,
    fontFamily: TEXT_ELEMENT_CONFIG.fontFamily,
    draggable: _.get(item, "draggable", false),
    fontSize: _.get(item, "fontSize", TEXT_ELEMENT_CONFIG.fontSize),
    text: _.get(item, "text", ""),
    lineHeight: 1,
    verticalAlign: "middle",
    align: isRTL ? "right" : "left",
    direction: isRTL ? "rtl" : "ltr",
  };
  const node = new konva.Text(props);

  if (canDrag) {
    maintainDraggableStateForTextNode(node, {
      stage,
      layer,
      transformer,
      konva,
    });
  }

  layer.add(node);
  return node;
};

/**
 *
 * @param {*} node
 * @param {*} konva
 * @returns is text node or not
 */
export const isTextNode = (node, konva) => {
  return node && node instanceof konva.Text;
};

/**
 *
 * @param {*} node
 * @param {*} { layer, konva }
 * @description maintain draggable state of text node
 */
const maintainDraggableStateForTextNode = (
  node,
  { stage, layer, transformer, konva }
) => {
  const id = node.id();

  const answersMapping = stage.getAttr("answersMapping");
  const isDroppedIn = _.includes(_.flatten(_.values(answersMapping)), id);

  const group = new konva.Group({
    id: `${id}_${CANVAS_IDS.TEXT_GROUP_ID}`,
    draggable: false,
  });
  layer.add(group);

  node.setOffset({
    x: 0,
    y: -1,
  });
  /**
   * text wrapper node
   */
  const wrapperNode = new konva.Rect({
    id: `${id}_${CANVAS_IDS.TEXT_WRAPPER_ID}`,
    x: node.x(),
    y: node.y(),
    width: node.width(),
    height: node.height(),
    fill: TEXT_ELEMENT_CONFIG.WRAPPER_FILL,
    stroke: TEXT_ELEMENT_CONFIG.WRAPPER_STROKE,
    strokeWidth: TEXT_ELEMENT_CONFIG.WRAPPER_STROKE_WIDTH,
    cornerRadius: TEXT_ELEMENT_CONFIG.WRAPPER_CORNER_RADIUS,
    listening: false,
    draggable: false,
  });
  if (!isDroppedIn) {
    wrapperNode.shadowColor(TEXT_ELEMENT_CONFIG.WRAPPER_SHADOW);
    wrapperNode.shadowBlur(TEXT_ELEMENT_CONFIG.WRAPPER_SHADOW_BLUR);
    wrapperNode.shadowOffsetY(TEXT_ELEMENT_CONFIG.WRAPPER_SHADOW_OFFSETY);
  }
  group.add(wrapperNode);

  if (transformer) {
    transformer.on(KONVA_EVENTS.TRANSFORM, () => {
      wrapperNode.x(node.x());
      wrapperNode.y(node.y());
      wrapperNode.height(node.height());
      wrapperNode.width(node.width());
    });
  }
  if (node) {
    node.on(KONVA_EVENTS.DRAGMOVE, () => {
      wrapperNode.x(node.x());
      wrapperNode.y(node.y());
    });
  }
};

/**
 *
 * @param { stage, transformer }
 * @description add text editing field on double click on Text Node
 */
let activeElementId = null;
export const createTextEditingField = (payload, t, mode) => {
  const { konva, stage, transformer, onTextChange = () => {} } = payload;
  if (!transformer) return;

  if (activeElementId) {
    // * un-focusing without save on any other canvas
    const textEditElement = document.getElementById(activeElementId);
    if (textEditElement) textEditElement.remove();
    stage.setAttr(KONVA_ATTR.IS_CHANGING_TEXT, undefined);
    activeElementId = null;
  }

  /**
   * @description
   * add dom element on text node to update node text
   */
  const addTextEditField = e => {
    const _node = e.target;
    const isLabelInEdit = _node && _.includes(_node.name(), "LABEL#LABEL");

    // * using target node for label node, else using transfromer attached node
    const node = isLabelInEdit ? _node : _.first(transformer.getNodes());
    if (!node || !isTextNode(node, konva)) return;

    const nodeId = node?.id();
    if (!node || !nodeId || !_.isFunction(node.setAttr)) return;

    const currentActive = stage?.getAttr(KONVA_ATTR.IS_CHANGING_TEXT);
    if (currentActive === nodeId) return;

    const isLabelElement = isLabelNode(node);

    const wrapperNodeId = `${nodeId}_${CANVAS_IDS.TEXT_WRAPPER_ID}`;
    const wrapperNode = stage.findOne(`#${wrapperNodeId}`);

    // * getting text area initial position
    const nodePosition = node.getAbsolutePosition();
    const stageBox = stage.container().getBoundingClientRect();
    const id = `${nodeId}_text_editor`;
    activeElementId = id;

    const areaPosition = {
      x: stageBox.left + nodePosition.x,
      y: stageBox.top + nodePosition.y - node.fontSize(),
    };

    const padY = node.padding() * (isLabelElement ? 0.75 : 0.4);

    // * Editing area dom attributes
    const paragraph = document.createElement("p");
    paragraph.id = id;
    paragraph.setAttribute("dir", "auto");
    paragraph.setAttribute("contenteditable", "true");
    paragraph.setAttribute("data-context-id", "konva-text-editor");
    paragraph.innerHTML = _.trim(node.text());

    // * Placeholder for editor
    const labelPlaceholder = t(
      mode === "create" ? "common:add_with_label" : "common:typeHere",
      {
        label: t("worksheet:answer"),
      }
    );
    const textPlaceholder = t("worksheet:add_text_here");
    const placeholder = isLabelElement ? labelPlaceholder : textPlaceholder;
    paragraph.setAttribute(`data-${id}-placeholder`, placeholder);
    const isPlaceholder = placeholder === paragraph.innerHTML;
    if (isPlaceholder) paragraph.innerHTML = "";
    const style = document.createElement("style");
    style.innerHTML = `
      #${id}:empty:before {
        content: attr(data-${id}-placeholder);
        color: ${TEXT_ELEMENT_CONFIG.PLACEHOLDER_FILL};
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);

    paragraph.style.position = "absolute";
    paragraph.style.zIndex = 9999;
    paragraph.style.top = `${areaPosition.y}px`;
    paragraph.style.left = `${areaPosition.x}px`;
    paragraph.style.backgroundColor = "transparent";
    paragraph.style.padding = `${padY}px ${node.padding()}px`;
    paragraph.style.outline = "none";
    paragraph.style.width = "auto";
    paragraph.style.height = node.height() + "px";
    paragraph.style.minHeight = node.height() + "px";
    paragraph.style.fontSize = `${node.fontSize()}px`;
    paragraph.style.whiteSpace = "nowrap";
    paragraph.style.fontFamily = node.fontFamily();
    paragraph.style.fontWeight = 500;
    document.body.appendChild(paragraph);
    paragraph.focus();
    node.opacity(0);
    stage.setAttr(KONVA_ATTR.IS_CHANGING_TEXT, nodeId);
    transformer.fire(KONVA_EVENTS.TRANSFORMERDETACHED, { evt: node }, true);

    // * selecting current text by default
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(paragraph);
    selection.removeAllRanges();
    selection.addRange(range);

    // * updating space and width of node
    const space = TEXT_ELEMENT_CONFIG.TEXT_EDITING_SPACE;
    const initWidth = paragraph.scrollWidth + space;
    node?.width(
      isLabelElement
        ? Math.max(getCanvasRelativeSize(150, false, false), initWidth)
        : initWidth
    );
    if (wrapperNode) wrapperNode.width(node.width());

    // * for now preventing multi-line text
    paragraph.addEventListener("keydown", function (event) {
      if (event.key === "Enter") {
        event.preventDefault();
      } else if (event.key == "Escape") {
        saveAndRemoveInput(event);
      }
    });

    // * updating content of node on input
    paragraph.addEventListener("input", function () {
      node.text(paragraph.textContent);

      if (_.isEmpty(_.trim(paragraph.textContent))) {
        // * Keeping cursor in alignment
        paragraph.innerHTML = "";
      }

      let newWidth = paragraph.scrollWidth + space;
      if (isLabelElement) {
        // * controlling minimum label width, when entering
        newWidth = Math.max(newWidth, getCanvasRelativeSize(150, false, false));
      }
      node.width(newWidth);
      if (wrapperNode) wrapperNode.width(node.width());
    });

    // * Update text saving function, invoked on document scroll and editor element blur event
    function saveAndRemoveInput(e) {
      if (_.get(e, "type") === "scroll" && isWorksheetWebView()) return null; // * to open app keyboard
      if (!activeElementId) return null;

      // * on completion, removing editor dom element and adding node element
      activeElementId = null;
      if (paragraph && document.body.contains(paragraph)) {
        document.body.removeChild(paragraph);
      }
      node.opacity(1);

      // * getting trimmed text, set text editing status to false and giving update payload call
      const text = _.trim(node.text());
      stage.setAttr(KONVA_ATTR.IS_CHANGING_TEXT, undefined);
      onTextChange(
        {
          id: nodeId,
          text: _.isEmpty(text)
            ? isLabelElement
              ? ""
              : t("worksheet:add_text_here")
            : text,
          x: node.x(),
          y: node.y(),
        },
        null
      );

      if (isLabelElement) {
        // * for label element adjusting node size after action
        const newWidth = getCanvasRelativeSize(150, false, false);
        node.width(newWidth);
        if (wrapperNode) wrapperNode.width(newWidth);
      }

      if (isLabelElement) {
        setTimeout(
          () => {
            // * after re-draw attaching transformer on label node
            transformer.fire(
              KONVA_EVENTS.TRANSFORMERATTACHED,
              { evt: node },
              true
            );
          },
          mode === "create" ? 100 : 0 // * waiting in create mode, instant for other
        );
      }

      window.removeEventListener("scroll", saveAndRemoveInput, true);
    }

    // * saving content and removing child on blur and scroll
    paragraph.addEventListener("blur", saveAndRemoveInput);
    window.addEventListener("scroll", saveAndRemoveInput, true);
  };

  if (mode === "create") stage.on(KONVA_EVENTS.DOUBLECLICK, addTextEditField);
  else stage.on(KONVA_EVENTS.ATTACHINPUT, addTextEditField);
};
